import React, { useState, useEffect } from 'react';
import { DataService } from '../services/dataService';
import { Pooja, SiteConfig } from '../types';
import { QrCode, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Poojas: React.FC = () => {
  const [poojas, setPoojas] = useState<Pooja[]>(DataService.getPoojas());
  const [config, setConfig] = useState<SiteConfig>(DataService.getConfig());
  const [showQR, setShowQR] = useState<Pooja | null>(null);
  const { language, t } = useLanguage();

  useEffect(() => {
    const handler = (e: any) => {
      try {
        const db = e?.detail?.db || DataService.getDB();
        if (db) {
          setConfig(db.config || DataService.getConfig());
          setPoojas(db.poojas || DataService.getPoojas());
        }
      } catch (err) {
        console.warn('Poojas db_updated handler error', err);
      }
    };

    window.addEventListener('db_updated', handler as EventListener);

    // initial sync
    try {
      const db = DataService.getDB();
      if (db) {
        setConfig(db.config || DataService.getConfig());
        setPoojas(db.poojas || DataService.getPoojas());
      }
    } catch (err) {
      console.warn('Poojas initial DB load error', err);
    }

    return () => window.removeEventListener('db_updated', handler as EventListener);
  }, []);

// redundant localStorage fallback removed

  useEffect(() => {
    setPoojas(DataService.getPoojas());
    setConfig(DataService.getConfig());
  }, []);

  // Generate a UPI link
  const getUPILink = (pooja: Pooja) => {
    const pa = (config.upiId || '').trim();
    const am = String(Math.max(1, Math.floor(Number(pooja.price || 0))));
    return `upi://pay?pa=${pa}&am=${am}&cu=INR`;
  };

  const getQRImageUrl = (upiLink: string, size: number = 200) => {
    const data = encodeURIComponent(upiLink);
    return `https://api.qrserver.com/v1/create-qr-code/?data=${data}&size=${size}x${size}&margin=0`;
  };

  const openUPIApp = (link: string) => {
    try {
      window.location.href = link;
    } catch {
      window.open(link, '_self');
    }
  };

  return (
    <div className="relative py-12 px-4 bg-gray-50 min-h-screen">
      <img
        src="/images/t1.jpg"
        alt=""
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none"
      />
      <div className="relative container mx-auto z-10">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-serif font-bold text-temple-orange-900 mb-4">{t('Poojas & Offerings', 'വഴിപാടുകൾ')}</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {t('Book your offerings online. Select a pooja to proceed with payment via UPI.', 'ഓൺലൈനായി വഴിപാടുകൾ ബുക്ക് ചെയ്യാം. യുപിഐ വഴി പണമടയ്ക്കാൻ താഴെ പറയുന്നവയിൽ നിന്ന് തിരഞ്ഞെടുക്കുക.')}
            <br/><span className="text-sm italic text-gray-500">{t('Note: Please share the payment screenshot to our WhatsApp number after payment.', 'കുറിപ്പ്: പണമടച്ച ശേഷം സ്ക്രീൻഷോട്ട് വാട്ട്‌സ്ആപ്പിൽ അയക്കുക.')}</span>
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(poojas ?? []).map((pooja) => (
            <div key={pooja.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all border border-gray-100 flex flex-col">
              <div className="bg-temple-orange-50 p-6 border-b border-orange-100">
                <h3 className="text-xl font-bold text-gray-800">
                  {language === 'ml' ? (pooja.nameMal || pooja.name) : pooja.name}
                </h3>
                <p className="text-sm text-temple-orange-700 font-medium mt-1">
                  {language === 'ml' ? (pooja.timingMal || pooja.timing) : pooja.timing}
                </p>
              </div>
              <div className="p-6 flex-grow">
                <p className="text-gray-600 mb-6">
                  {language === 'ml' ? (pooja.descriptionMal || pooja.description) : pooja.description}
                </p>
                <div className="flex justify-between items-center mt-auto">
                  <span className="text-2xl font-bold text-gray-800">₹{pooja.price}</span>
                  <button 
                    onClick={() => setShowQR(pooja)}
                    className="bg-temple-orange-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-temple-orange-700 transition-colors"
                  >
                    {t('Book Now', 'ബുക്ക് ചെയ്യുക')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* UPI QR Modal */}
      {showQR && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 relative animate-fade-in-up">
            <button 
              onClick={() => setShowQR(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
            
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                {t('Pay for', 'പണമടയ്ക്കുക:')} {language === 'ml' ? (showQR.nameMal || showQR.name) : showQR.name}
              </h3>
              <p className="text-gray-500 mb-6">{t('Scan with any UPI App', 'ഏതെങ്കിലും യുപിഐ ആപ്പ് ഉപയോഗിച്ച് സ്കാൻ ചെയ്യുക')}</p>
              
              <div className="bg-gray-100 p-4 rounded-xl inline-block mb-6">
                <img
                  src={getQRImageUrl(getUPILink(showQR), 220)}
                  alt="UPI QR"
                  className="w-[220px] h-[220px] mx-auto"
                  onError={(e) => { e.currentTarget.src = getQRImageUrl(getUPILink(showQR), 180); }}
                />
              </div>
              
              <div className="space-y-3">
                <p className="font-bold text-2xl text-temple-orange-600">₹{showQR.price}</p>
                <p className="text-xs text-gray-500 break-all border border-gray-200 p-2 rounded bg-gray-50">
                  {config.upiId}
                </p>
                <button
                  type="button"
                  disabled
                  className="block w-full bg-temple-orange-600 text-white py-3 rounded-xl font-bold opacity-60 cursor-not-allowed"
                >
                  Scan Using UPI App
                </button>
              </div>
              
              <p className="mt-4 text-xs text-gray-500">
                {t('After payment, WhatsApp the screenshot to', 'പണമടച്ച ശേഷം സ്ക്രീൻഷോട്ട് വാട്ട്‌സ്ആപ്പിൽ അയക്കുക')} <br/>
                <span className="font-bold text-gray-700">{config.whatsappNumber}</span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Poojas;
